// Logistics, fulfillment, Audience Data, and Ops mode help bodies.
// Extracted so modeHelpRegistry.tsx stays under the file-size hard cap.
import type { ReactNode } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";

/** Returns help body for ops/logistics/fulfillment modes, or null if not handled. */
export function getOpsHelpBody(mode: PrimaryMode): ReactNode | null {
  switch (mode) {
    case "logisticsRoutes":
      return (
        <p>
          Per-unit delivery routes — one route per shed, walking through draft → carrier-tentative →
          dispatch-scheduled → in-transit → delivered (or on-hold / cancelled). Each route has an
          ordered list of stops (plant origin → optional dealer holding lots → end customer) and
          an optional carrier (the hauler we book). Shares the Manufacturing Suite add-on
          entitlement.
        </p>
      );
    case "logisticsCalendar":
      return (
        <p>
          Week-at-a-glance delivery board. Each day shows how many routes are scheduled, in transit,
          or delivered. Click a day to list its routes, then open a route for the same detail card
          as the Routes mode (stops timeline, state transitions, evidence capture).
        </p>
      );
    case "logisticsCarriers":
      return (
        <p>
          The org's carrier roster — the haulers we book on routes.{" "}
          <strong>Contracted</strong> = standing partner. <strong>Freelance</strong> = per-job.{" "}
          <strong>Dealer BYO</strong> = the dealer hauls their own.{" "}
          <strong>In-house</strong> = our fleet. <strong>Other</strong> = catch-all. The Routes
          create-form picker pulls active carriers only, so deactivating here hides a carrier
          from new routes without breaking historical references.
        </p>
      );
    case "fulfillmentForms":
      return (
        <div className="space-y-2">
          <p>
            <strong>Forms</strong> is where sales fills manager-authored org forms —
            site surveys, acknowledgments, and other paperwork outside the fixed
            proposal package. Start a form from a template, edit the text fields,
            preview the PDF, and save it to storage.
          </p>
          <p>
            Templates are authored in Manager → Documents Editor → Documents.
            Lead and opportunity entry points land in a follow-up slice.
          </p>
        </div>
      );
    case "fulfillmentOrders":
      return (
        <div className="space-y-2">
          <p>
            <strong>Orders</strong> is the retail-side customer-order pipeline — every{" "}
            <code>ops_order_intake</code> row your org submitted, whether the order stays in your
            internal review queue or was routed upstream to a manufacturer. Open / Accepted /
            All filters replace spreadsheet order tracking for sales-floor orgs.
          </p>
          <p>
            Rows show deal-thread links when present (<code>lead_id</code>, linked opportunity, and
            materialized Ops deal after accept). Submit new orders from Sales → Order Intake.
          </p>
        </div>
      );
    case "fulfillmentInventory":
    case "fulfillmentIntake":
    case "fulfillmentEntry":
      return (
        <div className="space-y-2">
          <p>
            <strong>Fulfillment</strong> is the lot manager's bucket — the
            home for non-production inventory entry (cash-purchased and
            repo'd units that enter your lot outside the manufacturing
            pipeline), the three-tier inspection-intake form lifecycle,
            and the read-only unit list for your location.
          </p>
          <p>
            <strong>Inventory</strong> shows every unit whose{" "}
            <code>inventory_holder_org_id</code> is your org — with
            intake-pending badges on any unit whose intake form is not
            yet admin-approved.{" "}
            <strong>Intake forms</strong> is the queue, grouped by{" "}
            <em>needs-filling</em> / <em>needs-review</em> /{" "}
            <em>needs-work</em>, with the per-form filling UI and the
            admin review/approve flow. <strong>New inventory</strong> is
            the entry surface: single-piece form for a walk-up cash
            purchase, or bulk CSV import for a vendor spreadsheet.
          </p>
          <p>
            A unit at <code>intake:*</code> status is not sellable and
            not assignable to a customer order until the intake form is
            completed, reviewed, edited if necessary, approved by an
            admin, and any elected Tier-2 upgrade work is marked
            complete (planning doc §6.2). See{" "}
            <code>docs/planning/sales_fulfillment_bucket.md</code> for
            the full lifecycle.
          </p>
        </div>
      );
    case "audienceData":
      return (
        <p>
          <strong>Audience Data</strong> ingests Sales Feed CSVs into shared sale facts
          for Google and Meta audiences. Dealers can share end-customer identity from the
          settings gear (confirm before turning on). Manufacturer views hide that
          identity unless the selling dealer has consented. Sales Feed and Import share one slider.
          Data quality is the alert icon on Sales data — acknowledge to dismiss. Sales vs Unmatchable
          is a slider. Unrecognized lots can be matched, created, or sent up with{" "}
          <strong>Not our location</strong>. A created location is a normal org location. Manufacturer-owned
          lots map as manufacturer-direct. Bennett and ShedCor each see unmatched lots; a dealer-created
          location is not automatically on the manufacturer feed.{" "}
          <strong>New</strong> is live inbound until you acknowledge; CSV backfill is never New. Dealers
          see their lots on the manufacturer feed. Each event shows dealer, order, and name (end-customer
          only when you are the selling dealer or share is on). Destinations download hashed Google or Meta
          CSVs (Google A1 is Parameters:TimeZone=America/New_York; Conversion Name is an org list, default Offline Shed Sales). Destinations also host authenticated Google/Meta pull URLs.
          Retail vs manufacturing vs combined dollar totals sit at the top of Audience Data and on Ops Deals.
        </p>
      );
    case "operations":
      return (
        <p>
          <strong>Deals</strong> are live customer paperwork sets — checklists, inventory host
          webhook timeline, and org-wide <strong>tasks</strong>. <strong>Payables</strong> tracks
          manufacturer payback when a dealer collects more than the allowed deposit (steel full-pay
          / over-deposit), grouped by manufacturer and category. <strong>Books</strong> rolls issued
          purchase orders into period totals and a finished-serial consumption report (materials $
          plus why) for a manual QuickBooks upload, then stores a reconciliation event and supporting
          document. Name a deal to open an instance, not
          an archetype (author those in <strong>Deal Types</strong>). Settings enable automation when
          orders are submitted or delivered. Retail QuickBooks invoice push stays in Settings.
        </p>
      );
    case "dealTypes":
      return (
        <p>
          <strong>Deal Types</strong> author reusable product types (e.g. Shed sale, Steel) and sale types
          (Cash, Finance, RTO). On Finance, list lenders; on RTO, name the one active lessor
          (org data, never hardcoded). Author required/allowed paperwork on the product type <em>and</em> on
          the sale type; the deal uses the combined set. Creating a customer deal in <strong>Deals</strong> will later
          instantiate from these types (DT-2). Apply <code>docs/sql/ops_deal_types_dt1.sql</code> and{" "}
          <code>docs/sql/ops_deal_type_sale_slots_v1.sql</code> once per environment.
        </p>
      );
    case "dailyChecklist":
      return (
        <p>
          Author the <strong>daily operating checklist</strong> template Sales reps complete on the Leads tab:
          manual tasks and schedules, ad-logging scoring, stale-lead reminders (informational), optional{" "}
          <strong>acknowledgement blocking</strong> for forced workplace messages, org-wide manager nudges, and unified
          lead/user reminder cards (bell + checklist + lead editor). Saved config overrides any legacy Location Tools copy.
        </p>
      );
    case "physicalInventory":
      return (
        <p>
          Configure <strong>lot unit physical inventory</strong> for finished buildings on sales lots: cadence,
          photo slot labels, and (later) missing-unit exception review. Field capture lives in Fulfillment —
          this Ops surface owns policy. Distinct from Manufacturing materials counts.
        </p>
      );
    case "opsLookup":
      return (
        <p>
          <strong>Lookup</strong> finds sales, manufacturing orders (units), and service jobs in one place.
          Search filters the active Sales / Orders / Service tab; open a row for location, costs, and linked
          identity. Read-only — use <strong>Deals</strong> for paperwork and <strong>Service Board</strong>{" "}
          for dispatch. Report a bug while a row is selected so triage gets the focused ids.
        </p>
      );
    case "opsObligations":
      return (
        <p>
          <strong>Obligations</strong> stores utilities, internet, leases, mortgages, and other recurring
          services for the organization or a location — provider, payment link, dates, and source documents.
          The AI Assistant will query these records later; this screen is the operational source of truth.
        </p>
      );
    default:
      return null;
  }
}
